import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill, formatUGX } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  searchBar: { display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end", flexWrap: "wrap" },
  filterSelect: { padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", minWidth: "140px" }
};

const STATUS_TONES = {
  draft: "muted",
  approved: "info",
  ordered: "warning",
  delivered: "success",
  cancelled: "danger"
};

const NEXT_STATUSES = {
  draft:     [{ label: "Approve",       value: "approved" }],
  approved:  [{ label: "Mark Ordered",  value: "ordered"  }],
  ordered:   [{ label: "Mark Delivered",value: "delivered"}],
  delivered: [],
  cancelled: []
};

const BLANK = {
  orderNo: "", title: "", supplier: "", department: "", totalAmount: "", notes: ""
};

class PurchaseOrders extends Component {
  state = {
    orders: [],
    loading: true,
    search: "",
    statusFilter: "",
    open: false,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() {
    this.fetchOrders();
  }

  fetchOrders = async () => {
    this.setState({ loading: true });
    try {
      const { search, statusFilter } = this.state;
      const params = { limit: 100 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get("/procurement", { params });
      const d = res.data.data || res.data;
      this.setState({ orders: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  handleSave = async () => {
    const { form } = this.state;
    if (!form.title || !form.supplier) {
      this.setState({ error: "Title and supplier are required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      const payload = {
        ...form,
        totalAmount: form.totalAmount ? parseFloat(form.totalAmount) : null
      };
      await api.post("/procurement", payload);
      this.setState({ open: false, saving: false, form: { ...BLANK } });
      this.fetchOrders();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to create order.", saving: false });
    }
  };

  handleStatusChange = async (id, newStatus) => {
    if (!window.confirm(`Change status to "${newStatus}"?`)) return;
    try {
      await api.patch(`/procurement/${id}/status`, { status: newStatus });
      this.fetchOrders();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const { orders, loading, search, statusFilter, open, form, saving, error } = this.state;

    const columns = [
      { id: "orderNo", header: "Order No", accessorFn: o => o.orderNo || "—", meta: { variant: "mono" } },
      { id: "title", header: "Title", accessorFn: o => o.title || "—" },
      { id: "supplier", header: "Supplier", accessorFn: o => o.supplier || "—" },
      { id: "department", header: "Department", accessorFn: o => o.department || "—" },
      {
        id: "amount",
        header: "Amount (UGX)",
        accessorFn: o => o.totalAmount,
        cell: info => (info.getValue() != null ? formatUGX(info.getValue()) : "—"),
        meta: { align: "right" }
      },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const status = info.row.original.status || "draft";
          return <Pill tone={STATUS_TONES[status] || "muted"}>{status}</Pill>;
        }
      },
      { id: "date", header: "Date", accessorFn: o => (o.createdAt ? o.createdAt.substring(0, 10) : "—") },
      {
        id: "actions",
        header: "",
        cell: info => {
          const o = info.row.original;
          const nextActions = NEXT_STATUSES[o.status] || [];
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              {nextActions.map(na => (
                <Button
                  key={na.value}
                  size="sm"
                  color="info"
                  simple
                  onClick={() => this.handleStatusChange(o.id, na.value)}
                >
                  {na.label}
                </Button>
              ))}
              {nextActions.length === 0 && (
                <span style={{ fontSize: "12px", color: "#aaa" }}>—</span>
              )}
            </div>
          );
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Purchase Orders</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    Manage procurement requests and purchase orders
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={() => this.setState({ open: true, form: { ...BLANK }, error: null })}>
                    <Icon style={{ marginRight: "4px" }}>add_shopping_cart</Icon> New Order
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              <div className={classes.searchBar}>
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={search}
                  onChange={e => this.setState({ search: e.target.value })}
                  onKeyDown={e => e.key === "Enter" && this.fetchOrders()}
                  style={{ flex: 1, maxWidth: "280px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
                />
                <select
                  className={classes.filterSelect}
                  value={statusFilter}
                  onChange={e => this.setState({ statusFilter: e.target.value }, this.fetchOrders)}
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="approved">Approved</option>
                  <option value="ordered">Ordered</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <Button size="sm" onClick={this.fetchOrders} color="primary" style={{ marginBottom: "2px" }}>
                  Search
                </Button>
              </div>

              <DataTable
                columns={columns}
                data={orders}
                isLoading={loading}
                emptyState="No purchase orders found."
              />
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Create Purchase Order" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput
              labelText="Order Number"
              id="poOrderNo"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.orderNo, onChange: this.handleChange("orderNo"), placeholder: "e.g. PO-2024-001" }}
            />
            <CustomInput
              labelText="Title *"
              id="poTitle"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.title, onChange: this.handleChange("title"), placeholder: "e.g. Office Supplies Q1 2024" }}
            />
            <CustomInput
              labelText="Supplier *"
              id="poSupplier"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.supplier, onChange: this.handleChange("supplier"), placeholder: "Supplier name" }}
            />
            <CustomInput
              labelText="Department"
              id="poDept"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.department, onChange: this.handleChange("department"), placeholder: "Requesting department" }}
            />
            <CustomInput
              labelText="Total Amount (UGX)"
              id="poAmount"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.totalAmount, onChange: this.handleChange("totalAmount"), type: "number", min: "0", placeholder: "Amount in Uganda Shillings" }}
            />
            <CustomInput
              labelText="Notes"
              id="poNotes"
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: form.notes,
                onChange: this.handleChange("notes"),
                multiline: true,
                rows: 3,
                placeholder: "Optional notes or specifications..."
              }}
            />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Create Order"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

PurchaseOrders.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(PurchaseOrders));
